import React, { MouseEvent } from 'react';
import styled from 'styled-components';
import { themes } from 'kb-shared';
import { ComprehensionQuestion } from '../';

interface ContainerProps {
  showAnswer?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 28px 16px 28px 16px;
  ${({ showAnswer }: ContainerProps) => `
    background-color: ${showAnswer ? themes.colors.KBBeige : 'white'};
    box-shadow: ${showAnswer ? '' : '0 5px 12px 0 rgba(0, 0, 0, 0.2)'};
    :hover {
      box-shadow: ${showAnswer ? '' : '0 5px 12px 0 rgba(0, 0, 0, 0.4)'};
    }
  `}
  transition: all 300ms linear;
  @media screen and (min-width: ${themes.breakpoints.largePhonePortrait}) {
    flex-direction: 'row';
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 28px;
`;

const TitleText = styled.span`
  ${themes.fonts.DomaineDisplay};
  font-size: 24px;
  color: ${themes.colors.KBNight};
`;

const SubtitleText = styled.span`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
  padding-top: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding-top: 14px;
  @media screen and (min-width: ${themes.breakpoints.largePhonePortrait}) {
    padding-top: 0px;
  }
`;

const LongOptionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 14px;
  @media screen and (min-width: ${themes.breakpoints.largePhonePortrait}) {
    padding-top: 0px;
  }
`;

const LongOptionDescriptionText = styled.span`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
`;

const OptionAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const AnswerBox = styled.div`
  background-color: white;
  padding: 16px;
  margin-top: 16px;
`;

const AnswerText = styled.span`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
`;

type Props = {
  question: ComprehensionQuestion;
  onSelect: (option: string) => void;
  showCorrectAnswer: boolean;
};

type State = {
  selectedOption: string | null;
};

export default class ComprehensionQuestionBox extends React.Component<Props, State> {
  state = {
    selectedOption: null
  };

  renderOption(option: string, index: number, useLabel: boolean) {
    const text = useLabel ? `Option ${index + 1}` : option;
    const { showCorrectAnswer } = this.props;
    const { correctResponse } = this.props.question;

    let selected;
    if (showCorrectAnswer) {
      selected = option === correctResponse;
    } else {
      selected = this.state.selectedOption === option;
    }
    return (
      <Option
        key={option}
        text={text}
        answerMode={this.props.showCorrectAnswer}
        selected={selected}
        onClick={() => {
          if (this.props.showCorrectAnswer) {
            return null;
          }
          this.setState({
            selectedOption: option
          });
          this.props.onSelect(option);
        }}
      />
    );
  }

  renderChoices() {
    const { options } = this.props.question;

    // If any of the options are over 40 characters, then display then as "(Option 1) Text"
    let requiresOptionLabels = false;
    options.forEach(element => {
      if (element.length >= 50) {
        requiresOptionLabels = true;
        return;
      }
    });
    if (requiresOptionLabels) {
      return (
        <LongOptionButtonContainer>
          {options.map((option: string, index: number) => {
            return (
              <OptionAndDescriptionContainer key={option}>
                {this.renderOption(option, index, true)}
                <LongOptionDescriptionText>{option}</LongOptionDescriptionText>
              </OptionAndDescriptionContainer>
            );
          })}
        </LongOptionButtonContainer>
      );
    } else {
      return (
        <ButtonContainer>
          {options.map((option: string, index: number) => {
            return this.renderOption(option, index, false);
          })}
        </ButtonContainer>
      );
    }
  }

  renderAnswer() {
    if (this.props.showCorrectAnswer) {
      return (
        <AnswerBox>
          <AnswerText>{this.props.question.explanation}</AnswerText>
        </AnswerBox>
      );
    }
  }

  render() {
    const { options, text } = this.props.question;
    let description = 'Select from one of the options below:';
    if (options.length === 2 && options.includes('true') && options.includes('false')) {
      description = 'Is this statement true or false?';
    }
    return (
      <Container showAnswer={this.props.showCorrectAnswer}>
        <TextContainer>
          <TitleText>{text}</TitleText>
          <SubtitleText>{description}</SubtitleText>
        </TextContainer>
        {this.renderChoices()}
        {this.renderAnswer()}
      </Container>
    );
  }
}

interface StyledProps {
  answerMode?: boolean;
  selected?: boolean;
}

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 14px;
  min-width: 80px;
  margin: 8px;
  padding-left: 12px;
  padding-right: 12px;
  cursor: not-allowed;

  ${({ answerMode }: StyledProps) => `
    cursor: ${answerMode ? 'not-allowed' : 'pointer'};
    :hover {
      ${answerMode ? '' : `box-shadow: 0px 0px 0px 2px ${themes.colors.KBNight} inset`};
    }
    ${answerMode ? '' : `box-shadow: 0px 0px 0px 1px ${themes.colors.KBNight} inset`};
  `}
  ${({ answerMode, selected }: StyledProps) => {
    if (answerMode) {
      return `
        background-color: ${selected ? themes.colors.KBYellow : themes.colors.KBTan};
      `;
    } else {
      return `
        background-color: ${selected ? themes.colors.KBNight : 'white'};
      `;
    }
  }}
`;

const OptionTitleText = styled.span`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  ${({ answerMode, selected }: StyledProps) => {
    if (answerMode) {
      return `
        color: ${selected ? themes.colors.KBNight : themes.colors.KBTwilightLight};
      `;
    } else {
      return `
        color: ${selected ? 'white' : themes.colors.KBNight};
      `;
    }
  }}
`;

const Option = (props: {
  text: string;
  selected: boolean;
  answerMode: boolean;
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
}) => {
  return (
    <OptionContainer
      selected={props.selected}
      onClick={props.onClick}
      answerMode={props.answerMode}
    >
      <OptionTitleText selected={props.selected} answerMode={props.answerMode}>
        {props.text.toUpperCase()}
      </OptionTitleText>
    </OptionContainer>
  );
};
